import AsyncStorage from '@react-native-async-storage/async-storage';
import React, { ReactNode, useEffect, useRef, useState } from 'react';

interface IToolTipAutoShowEntry {
  hasShown: boolean;
}

type TToolTipAutoShowStates<T extends string> = { [key in T]: IToolTipAutoShowEntry };

type TToolTipAutoShowContext<T extends string> = {
  toolTipAutoShowStates: TToolTipAutoShowStates<T>;
  markToolTipAsSeen: (key: T | T[], isSeen?: boolean) => void;
  autoShowQueue: T[];
  addToAutoShowQueue: (tooltip: T) => void;
};

const initialize = async <T extends string>(tooltipCollection: T[]): Promise<TToolTipAutoShowStates<T>> => {
  await Promise.all(
    tooltipCollection.map(async toolTipKey => {
      try {
        const result = await AsyncStorage.getItem(toolTipKey);
        if (!result) {
          // Entry does not exist in storage so we add it.
          const entry: IToolTipAutoShowEntry = { hasShown: false };
          await AsyncStorage.setItem(toolTipKey, JSON.stringify(entry));
        }
      } catch (error) {
        console.log('Error when adding default value to tooltip storage', error);
      }
    })
  );

  return updateStates<T>(tooltipCollection);
};

const updateStates = async <T extends string>(tooltipCollection: T[]): Promise<TToolTipAutoShowStates<T>> => {
  const entries = await Promise.all(
    tooltipCollection.map(async toolTipKey => {
      let entry:
        | {
            state: IToolTipAutoShowEntry;
            key: T;
          }
        | undefined;

      try {
        const entryString = await AsyncStorage.getItem(toolTipKey);
        if (!entryString) {
          entry = { state: { hasShown: false }, key: toolTipKey as T };
        } else {
          entry = {
            state: JSON.parse(entryString),
            key: toolTipKey as T,
          };
        }
      } catch (error) {
        console.error('Error when updating ToolTipAutoShowContextProvider state', error);
        entry = { state: { hasShown: false }, key: toolTipKey as T };
      }

      return entry;
    })
  );

  const states = entries.reduce((a, v) => ({ ...a, [v.key]: v.state }), {}) as TToolTipAutoShowStates<T>;
  return states;
};

const getTempState = <T extends string>(tooltipCollection: T[]): TToolTipAutoShowStates<T> => {
  const states = tooltipCollection.reduce((a, v) => {
    const state: IToolTipAutoShowEntry = {
      hasShown: true,
    };
    return { ...a, [v]: state };
  }, {}) as TToolTipAutoShowStates<T>;
  return states;
};

const markAsSeen = async <T extends string>(key: T, isSeen: boolean): Promise<IToolTipAutoShowEntry> => {
  let newState: IToolTipAutoShowEntry = { hasShown: true };
  try {
    const oldStateString = await AsyncStorage.getItem(key);
    if (oldStateString) {
      newState = {
        ...JSON.parse(oldStateString),
        hasShown: isSeen,
      };
    }
  } catch (error) {
    console.error('Error when marking tool tip as seen', error);
  }

  await AsyncStorage.setItem(key, JSON.stringify(newState));
  return newState;
};

const ToolTipAutoShowContext = React.createContext<TToolTipAutoShowContext<any> | undefined>(undefined);

const ToolTipAutoShowContextProvider = <T extends string>({ children, tooltipCollection }: { children?: ReactNode; tooltipCollection: T[] }) => {
  const [toolTipAutoShowStates, setToolTipAutoShowStates] = useState<TToolTipAutoShowStates<T>>(getTempState<T>(tooltipCollection));
  const [autoShowQueue, setAutoShowQueue] = useState<T[]>([]);
  const debounceRef = useRef<NodeJS.Timeout>();
  const debounceCollector = useRef<T[]>([]);

  useEffect(() => {
    const initState = async () => {
      const initialState = await initialize<T>(tooltipCollection);
      setToolTipAutoShowStates(initialState);
    };
    initState();
  }, [tooltipCollection]);

  async function markToolTipAsSeen(key: T | T[], isSeen: boolean = true) {
    if (Array.isArray(key)) {
      await markToolTipsAsSeen(key, isSeen);
    } else {
      await markToolTipsAsSeen([key], isSeen);
    }
  }

  async function markToolTipsAsSeen(keys: T[], isSeen: boolean) {
    const newStates = { ...toolTipAutoShowStates };
    let newAutoShowQueue = autoShowQueue;
    for (let key of keys) {
      const newState = await markAsSeen<T>(key, isSeen);
      newStates[key] = newState;
      newAutoShowQueue = newAutoShowQueue.filter(thisTooltip => thisTooltip !== key);
    }
    setToolTipAutoShowStates(newStates);
    setAutoShowQueue(newAutoShowQueue);
  }

  async function addToAutoShowQueue(tooltip: T) {
    if (debounceCollector.current.includes(tooltip) || autoShowQueue.includes(tooltip)) {
      return;
    }
    // we used debounce in case multi components called addToAutoShowQueue at the same time
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceCollector.current.push(tooltip);

    debounceRef.current = setTimeout(() => {
      setAutoShowQueue(tooltips => [...tooltips, ...debounceCollector.current]);
      debounceCollector.current = [];
    }, 100) as unknown as NodeJS.Timeout;
  }

  const value: TToolTipAutoShowContext<T> = {
    toolTipAutoShowStates,
    markToolTipAsSeen,
    autoShowQueue,
    addToAutoShowQueue,
  };

  return <ToolTipAutoShowContext.Provider value={value}>{children}</ToolTipAutoShowContext.Provider>;
};

export { ToolTipAutoShowContextProvider, ToolTipAutoShowContext };
export type { TToolTipAutoShowContext };
